import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useTranslation } from "@/hooks/use-translation"
import type { TimelinePlaybackControls } from "@/lib/timeline"
import { cn } from "@/lib/utils"

type PlayerIconButtonProps = {
  label: string
  onClick: () => void
  icon: ReactNode
  disabled?: boolean
  activeState?: boolean
}

export function PlayerIconButton({ label, onClick, icon, disabled, activeState }: PlayerIconButtonProps) {
  return (
    <ActionHintTooltip label={label}>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={cn(
          "rounded-sm p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
          activeState
            ? "bg-ui-surface-raised text-ui-accent-text"
            : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
        )}
      >
        {icon}
      </button>
    </ActionHintTooltip>
  )
}

type PlaybackTransportProps = {
  playing: boolean
  onTogglePlay: () => void
  onStepFrames: (frames: number) => void
  onGoToStart: () => void
  onGoToEnd: () => void
  className?: string
  children?: ReactNode
}

/** Ряд «в начало / кадр / play / в конец». Доп. кнопки (loop, монтаж) — через children. */
export function PlaybackTransport({
  playing,
  onTogglePlay,
  onStepFrames,
  onGoToStart,
  onGoToEnd,
  className,
  children,
}: PlaybackTransportProps) {
  const { t } = useTranslation("shot")

  return (
    <div className={cn("flex shrink-0 items-center justify-center gap-0.5", className)}>
      <PlayerIconButton label={t("player.toStart")} onClick={onGoToStart} icon={<ChevronFirst className="h-4 w-4" />} />
      <PlayerIconButton
        label={t("player.frameBack")}
        onClick={() => onStepFrames(-1)}
        icon={<ChevronLeft className="h-4 w-4" />}
      />
      <PlayerIconButton
        label={playing ? t("player.pause") : t("player.play")}
        onClick={onTogglePlay}
        icon={playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      />
      <PlayerIconButton
        label={t("player.frameForward")}
        onClick={() => onStepFrames(1)}
        icon={<ChevronRight className="h-4 w-4" />}
      />
      <PlayerIconButton label={t("player.toEnd")} onClick={onGoToEnd} icon={<ChevronLast className="h-4 w-4" />} />
      {children}
    </div>
  )
}

/**
 * Транспорт плеера: пауза сбрасывает ускорение, шаг кадра и «в конец» считаются от текущего frameMs.
 * getEndMs читается в момент клика — плеер целится в линейку, редактор в живой документ.
 */
export function usePlaybackTransport({
  controls,
  playing,
  frameMs,
  getEndMs,
  active = true,
}: {
  controls: TimelinePlaybackControls
  playing: boolean
  frameMs: number
  getEndMs: () => number
  /** false — пауза и сброс ускорения (плеер скрыт или неактивен). */
  active?: boolean
}) {
  const [forwardRate, setForwardRate] = useState(1)
  const frameMsRef = useRef(frameMs)
  frameMsRef.current = frameMs
  const getEndMsRef = useRef(getEndMs)
  getEndMsRef.current = getEndMs

  useEffect(() => {
    if (!active) {
      controls.pause()
      setForwardRate(1)
    }
  }, [active, controls])

  const pauseAll = useCallback(() => {
    controls.pause()
    setForwardRate(1)
  }, [controls])

  const togglePlay = useCallback(() => {
    if (playing) pauseAll()
    else {
      setForwardRate(1)
      controls.play(1)
    }
  }, [playing, pauseAll, controls])

  const stepFrames = useCallback(
    (frames: number) => {
      pauseAll()
      controls.seek(controls.currentMs() + frames * frameMsRef.current)
    },
    [pauseAll, controls],
  )

  const stepMs = useCallback(
    (deltaMs: number) => {
      pauseAll()
      controls.seek(controls.currentMs() + deltaMs)
    },
    [pauseAll, controls],
  )

  const goToStart = useCallback(() => {
    pauseAll()
    controls.seek(0)
  }, [pauseAll, controls])

  const goToEnd = useCallback(() => {
    pauseAll()
    controls.seek(Math.max(0, getEndMsRef.current() - frameMsRef.current))
  }, [pauseAll, controls])

  const playForward = useCallback(() => {
    const rate = playing && controls.getRate() > 0 ? Math.min(controls.getRate() * 2, 8) : 1
    setForwardRate(rate)
    controls.play(rate)
  }, [playing, controls])

  const playReverse = useCallback(() => {
    const rate = playing && controls.getRate() < 0 ? Math.max(controls.getRate() * 2, -4) : -1
    setForwardRate(1)
    controls.play(rate)
  }, [playing, controls])

  return {
    forwardRate,
    pauseAll,
    togglePlay,
    stepFrames,
    stepMs,
    goToStart,
    goToEnd,
    playForward,
    playReverse,
  }
}
